import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import { UkDateInput } from './ActivationDashboard';
import {
  ALL_TIME_FROM,
  CHANNELS,
  ChannelId,
  Counts,
  DEFAULT_TARGETS,
  DateRange,
  PRESETS,
  PresetId,
  Step,
  StepId,
  Targets,
  addCounts,
  blockerOf,
  channelOf,
  emptyCounts,
  presetRange,
  shiftRange,
  stepsOf,
  channelOfCode,
} from './acquisitionFunnel';
import './activation-dashboard.scss';
import './onboarding-overview.scss';
import './acquisition-dashboard.scss';

/**
 * Engagement → Acquisition: Charlie's acquisition funnel built only from
 * the Onboarding tab's endpoints, one request per source or code, summed
 * here into channels. Paying, day-31 retention, business stages, spend and
 * website routing need backend work, so they show as not tracked.
 */

interface FilterOption {
  key: string;
  label: string;
  count: number;
}

interface CodeOption extends FilterOption {
  code: string;
  source: string;
  /** Channel set on the code in Campaign Codes; null = derive from source. */
  channel: string | null;
}

interface FiltersResponse {
  sources: FilterOption[];
  codes: CodeOption[];
}

interface OverviewResponse {
  dailySignups: Array<{ day: string; signups: number }>;
  funnel: {
    sessions: number;
    firstMessage: number;
    completed: number;
    retention: { d7: { retained: number; eligible: number } };
  };
}

interface LinkClicksResponse {
  clicks: number;
  taps: number;
  trackedSince: string | null;
}

interface Leaf {
  /** `code:<acquisition_codes.id>` or `src:<source key>` */
  key: string;
  label: string;
  count: number;
}

interface SourceNode {
  key: string;
  label: string;
  count: number;
  hasCodes: boolean;
  leaves: Leaf[];
  /** All codes of the source, across channels (leaves may be a subset). */
  totalLeaves: number;
}

interface ChannelNode {
  id: ChannelId;
  label: string;
  color: string;
  sources: SourceNode[];
}

/** One request's worth of cohort: a source, optionally narrowed to codes. */
interface Unit {
  id: string;
  source: string;
  codes: string[] | null;
  label: string;
  channel: ChannelId;
}

type ViewBy = 'channel' | 'source' | 'code';
type View = 'funnel' | 'bottlenecks' | 'analysis';
type StageId = 'tof' | 'tap' | StepId;

const isInviteStage = (id: StageId) => id === 'tof' || id === 'tap';

interface Row {
  key: string;
  label: string;
  color: string;
  cur: Counts;
  prev: Counts | null;
}

const PALETTE = [
  '#2563eb',
  '#0d9488',
  '#d97706',
  '#7c3aed',
  '#db2777',
  '#16a34a',
  '#ea580c',
  '#0891b2',
  '#64748b',
  '#94a3b8',
];

const STAGES: Array<{ id: StageId; name: string; sub: string }> = [
  { id: 'tof', name: 'Top of funnel', sub: 'Invite page views' },
  { id: 'tap', name: 'Tapped WhatsApp', sub: 'Tapped Start on WhatsApp' },
  { id: 'mql', name: 'MQL', sub: 'Conversation started' },
  { id: 'mql2', name: 'MQL2', sub: 'First message sent' },
  { id: 'onb', name: 'Onboarded', sub: 'Onboarding complete' },
  { id: 'ret', name: 'Retained', sub: 'Messaged on day 7' },
];

const LOCKED_STAGES = [
  'Paying',
  'SQL',
  'Opportunity',
  'Trial',
  'Closed won',
] as const;

const STEP_LABELS: Record<StepId, string> = {
  mql: 'View → MQL',
  mql2: 'MQL → MQL2',
  onb: 'MQL2 → Onboarded',
  ret: 'Onboarded → Retained',
};

const BY_LABELS: Record<ViewBy, string> = {
  channel: 'By channel',
  source: 'By source',
  code: 'By code',
};

const TARGETS_KEY = 'platformStats.acquisition.targets';

const todayIso = () => new Date().toISOString().slice(0, 10);

const fmtDay = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

const fmtRange = (r: DateRange) =>
  r.from === r.to
    ? `${fmtDay(r.from)} ${r.from.slice(0, 4)}`
    : r.from.slice(0, 4) === r.to.slice(0, 4)
      ? `${fmtDay(r.from)} – ${fmtDay(r.to)} ${r.to.slice(0, 4)}`
      : `${fmtDay(r.from)} ${r.from.slice(0, 4)} – ${fmtDay(r.to)} ${r.to.slice(0, 4)}`;

const pct = (n: number | null) =>
  n == null ? '—' : `${n < 0.1 ? (n * 100).toFixed(1) : Math.round(n * 100)}%`;

const loadTargets = (): Targets => {
  try {
    const saved = JSON.parse(localStorage.getItem(TARGETS_KEY) ?? 'null');
    return saved ? { ...DEFAULT_TARGETS, ...saved } : DEFAULT_TARGETS;
  } catch {
    return DEFAULT_TARGETS;
  }
};

/**
 * Channel → sources → codes. A campaign source whose codes carry different
 * channels appears under each of them with just that channel's codes, so a
 * code's channel (set in Campaign Codes) wins over its source's default.
 */
const buildTree = (filters: FiltersResponse): ChannelNode[] =>
  CHANNELS.map((ch) => ({
    ...ch,
    sources: filters.sources.flatMap((s): SourceNode[] => {
      const all = filters.codes.filter((c) => c.source === s.key);
      if (all.length === 0) {
        return channelOf(s.key) === ch.id
          ? [
              {
                key: s.key,
                label: s.label,
                count: s.count,
                hasCodes: false,
                leaves: [
                  { key: `src:${s.key}`, label: s.label, count: s.count },
                ],
                totalLeaves: 1,
              },
            ]
          : [];
      }
      const codes = all.filter(
        (c) => channelOfCode(s.key, c.channel) === ch.id,
      );
      if (codes.length === 0) return [];
      return [
        {
          key: s.key,
          label: s.label,
          count: codes.reduce((acc, c) => acc + c.count, 0),
          hasCodes: true,
          leaves: codes.map((c) => ({
            key: `code:${c.key}`,
            label: `${c.label} · DREAM-${c.code}`,
            count: c.count,
          })),
          totalLeaves: all.length,
        },
      ];
    }),
  })).filter((ch) => ch.sources.length > 0);

const unitsFor = (
  tree: ChannelNode[],
  selected: Set<string> | null,
  by: ViewBy,
): Unit[] =>
  tree.flatMap((ch) =>
    ch.sources.flatMap((s): Unit[] => {
      const picked = s.leaves.filter((l) => !selected || selected.has(l.key));
      if (picked.length === 0) return [];
      if (by === 'code') {
        return picked.map((l) => ({
          id: l.key,
          source: s.key,
          codes: s.hasCodes ? [l.key.slice(5)] : null,
          label: l.label,
          channel: ch.id,
        }));
      }
      const narrowed = s.hasCodes && picked.length < s.totalLeaves;
      return [
        {
          id: `src:${s.key}${narrowed ? `|${picked.map((l) => l.key).join(',')}` : ''}`,
          source: s.key,
          codes: narrowed ? picked.map((l) => l.key.slice(5)) : null,
          label: s.label,
          channel: ch.id,
        },
      ];
    }),
  );

const unitQuery = (u: Unit) =>
  [
    `source=${encodeURIComponent(u.source)}`,
    ...(u.codes ?? []).map((c) => `code=${encodeURIComponent(c)}`),
  ].join('&');

const cacheKey = (r: DateRange, u: Unit) => `${r.from}|${r.to}|${unitQuery(u)}`;

const toCounts = (
  ov: OverviewResponse,
  lc: LinkClicksResponse | null,
): Counts => ({
  clicks: lc?.clicks ?? 0,
  taps: lc?.taps ?? 0,
  mql: ov.funnel.sessions,
  mql2: ov.funnel.firstMessage,
  onb: ov.funnel.completed,
  retained: ov.funnel.retention.d7.retained,
  retEligible: ov.funnel.retention.d7.eligible,
  daily: Object.fromEntries(ov.dailySignups.map((d) => [d.day, d.signups])),
});

/** Runs async jobs a few at a time so a wide view doesn't flood the API. */
const runLimited = async (jobs: Array<() => Promise<void>>, limit: number) => {
  let next = 0;
  const worker = async () => {
    while (next < jobs.length) await jobs[next++]();
  };
  await Promise.all(Array.from({ length: limit }, worker));
};

const Delta: FC<{ cur: number; prev: number | undefined }> = ({
  cur,
  prev,
}) => {
  if (prev == null) return null;
  const d = cur - prev;
  return (
    <span
      className={`acq-delta ${d > 0 ? 'up' : d < 0 ? 'down' : ''}`}
      title={`Previous period: ${prev}`}
    >
      {d > 0 ? `▲${d}` : d < 0 ? `▼${-d}` : '='}
    </span>
  );
};

const TriBox: FC<{
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  label: string;
}> = ({ checked, indeterminate, onChange, label }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type='checkbox'
      aria-label={label}
      checked={checked}
      onChange={onChange}
    />
  );
};

/** Channel → source → code tick-box picker with search and "Only". */
const ChannelPicker: FC<{
  tree: ChannelNode[];
  selected: Set<string> | null;
  onChange: (next: Set<string> | null) => void;
}> = ({ tree, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const leavesOf = (s: SourceNode) => s.leaves.map((l) => l.key);
  const channelLeaves = (ch: ChannelNode) => ch.sources.flatMap(leavesOf);
  const all = tree.flatMap(channelLeaves);
  const isOn = (key: string) => !selected || selected.has(key);
  const onCount = all.filter(isOn).length;

  const setKeys = (keys: string[], on: boolean) => {
    const next = new Set(selected ?? all);
    for (const k of keys) {
      if (on) next.add(k);
      else next.delete(k);
    }
    onChange(all.every((k) => next.has(k)) ? null : next);
  };
  const only = (keys: string[]) => onChange(new Set(keys));
  const toggleExpand = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const q = query.trim().toLowerCase();
  const matches = (label: string) => !q || label.toLowerCase().includes(q);

  const activeChannels = tree.filter((ch) => channelLeaves(ch).some(isOn));
  const summary =
    onCount === all.length
      ? 'All channels'
      : onCount === 0
        ? 'Nothing selected'
        : activeChannels.length === 1
          ? `${activeChannels[0].label}${
              channelLeaves(activeChannels[0]).every(isOn) ? '' : ' (part)'
            }`
          : `${activeChannels.length} channels`;

  const row = (
    key: string,
    keys: string[],
    label: React.ReactNode,
    text: string,
    depth: number,
    expandable: boolean,
    isOpen: boolean,
  ) => {
    const on = keys.filter(isOn).length;
    return (
      <div className={`acq-picker-row depth-${depth}`} key={key}>
        {expandable ? (
          <button
            type='button'
            className={`acq-expand${isOpen ? ' open' : ''}`}
            aria-label={`Show ${text}`}
            aria-expanded={isOpen}
            onClick={() => toggleExpand(key)}
          >
            ▸
          </button>
        ) : (
          <span className='acq-expand-spacer' />
        )}
        <label className='filter-menu-option'>
          <TriBox
            label={text}
            checked={on === keys.length}
            indeterminate={on > 0 && on < keys.length}
            onChange={() => setKeys(keys, on !== keys.length)}
          />
          {label}
        </label>
        <button type='button' className='acq-only' onClick={() => only(keys)}>
          Only
        </button>
      </div>
    );
  };

  return (
    <div
      className={`cost-filter source-filter${onCount < all.length ? ' filtered' : ''}`}
      ref={rootRef}
    >
      <button
        type='button'
        className='cost-filter-toggle'
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label='Filter by channel'
        onClick={() => setOpen((o) => !o)}
      >
        <span className='filter-summary'>{summary}</span>
        <span className='filter-caret' aria-hidden='true'>
          ▾
        </span>
      </button>
      {open && (
        <div className='cost-filter-menu acq-picker-menu'>
          <input
            className='acq-picker-search'
            placeholder='Search channels, sources and codes'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className='filter-menu-actions'>
            <button
              type='button'
              disabled={onCount === all.length}
              onClick={() => onChange(null)}
            >
              Select all
            </button>
            <button
              type='button'
              disabled={onCount === 0}
              onClick={() => onChange(new Set())}
            >
              Clear
            </button>
          </div>
          {tree.map((ch) => {
            const sources = ch.sources.filter(
              (s) =>
                matches(ch.label) ||
                matches(s.label) ||
                s.leaves.some((l) => matches(l.label)),
            );
            if (sources.length === 0) return null;
            const chOpen = !!q || expanded.has(ch.id);
            const total = ch.sources.reduce((n, s) => n + s.count, 0);
            return (
              <div key={ch.id} className='acq-picker-channel'>
                {row(
                  ch.id,
                  channelLeaves(ch),
                  <>
                    <i className='acq-dot' style={{ background: ch.color }} />
                    <span>
                      {ch.label} ({total})
                    </span>
                  </>,
                  ch.label,
                  0,
                  true,
                  chOpen,
                )}
                {chOpen &&
                  sources.map((s) => {
                    const srcOpen = !!q || expanded.has(s.key);
                    return (
                      <React.Fragment key={s.key}>
                        {row(
                          s.key,
                          leavesOf(s),
                          <span>
                            {s.label} ({s.count})
                          </span>,
                          s.label,
                          1,
                          s.hasCodes,
                          srcOpen,
                        )}
                        {s.hasCodes &&
                          srcOpen &&
                          s.leaves
                            .filter(
                              (l) =>
                                matches(l.label) ||
                                matches(s.label) ||
                                matches(ch.label),
                            )
                            .map((l) =>
                              row(
                                l.key,
                                [l.key],
                                <span>
                                  {l.label} ({l.count})
                                </span>,
                                l.label,
                                2,
                                false,
                                false,
                              ),
                            )}
                      </React.Fragment>
                    );
                  })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CHANNEL_NOTES: Record<ChannelId, string> = {
  meta: 'Meta ad campaign codes, including ads that open WhatsApp directly.',
  groups: 'Campaign codes shared in communities and groups.',
  creators: 'The Creators code, plus referral links from UGC creator accounts.',
  referrals:
    'Referral links and invites from Dream users and Dream staff, including WhatsApp invites Dream sends on their behalf.',
  organic: 'Organisation invites, the Website code, and anyone with no source.',
  other: 'Campaign codes not yet assigned to a channel.',
};

const COLUMN_NOTES: Array<[string, string]> = [
  [
    'Top of funnel',
    'Invite page views, counted by the browser after the page loads, with bots and link previews removed. Counts views, not people. Ads that open WhatsApp directly and WhatsApp invites have no page, so show none.',
  ],
  [
    'Tapped WhatsApp',
    'Taps on Start on WhatsApp on an invite page. On the team app’s /ref page, WhatsApp opening by itself after a few seconds also counts.',
  ],
  [
    'MQL',
    'Onboarding started: messaged Dream with a code or link, or was sent a WhatsApp invite (counted when sent, before they reply).',
  ],
  ['MQL2', 'Sent their first message.'],
  ['Onboarded', 'Finished onboarding.'],
  [
    'Retained',
    'Messaged on day 7 after onboarding. Only counts people onboarded 8+ days ago.',
  ],
  [
    'Blocker',
    'The step furthest below its target, ignoring steps with fewer than 5 people.',
  ],
];

const FunnelKey: FC<{ tree: ChannelNode[]; dots: boolean }> = ({
  tree,
  dots,
}) => (
  <details className='acq-unlock acq-key'>
    <summary>
      <strong>Key</strong>
    </summary>
    <dl>
      {tree.map((ch) => (
        <React.Fragment key={ch.id}>
          <dt>
            {dots && <i className='acq-dot' style={{ background: ch.color }} />}
            {ch.label}
          </dt>
          <dd>
            {CHANNEL_NOTES[ch.id]} Includes{' '}
            {ch.sources.map((s) => s.label).join(', ')}.
          </dd>
        </React.Fragment>
      ))}
    </dl>
    <dl>
      {COLUMN_NOTES.map(([name, note]) => (
        <React.Fragment key={name}>
          <dt>{name}</dt>
          <dd>{note}</dd>
        </React.Fragment>
      ))}
    </dl>
    <p>
      The % under each number is conversion from the previous column against its
      target: green on target, amber within 75% of it, red below, grey under 5
      people.
    </p>
  </details>
);

const NotTracked: FC = () => (
  <div className='acq-unlock'>
    <strong>Not tracked yet, and what would unlock it</strong>
    <ul>
      <li>
        <b>Paying and Retained at day 31:</b> a backend change so this page can
        count subscriptions and day-31 activity per channel. Retained shows day
        7 until then.
      </li>
      <li>
        <b>SQL, Opportunity, Trial and Closed won:</b> these are about companies
        rather than campaign codes. They need the definitions agreed with
        Charlie, then a backend change.
      </li>
      <li>
        <b>Spend, cost per sign-up and Meta clicks:</b> read-only access to the
        Meta ad account. Click-to-WhatsApp ads open WhatsApp directly, so Dream
        never sees those clicks.
      </li>
      <li>
        <b>Website vs straight to WhatsApp:</b> point the website&apos;s
        WhatsApp buttons at the existing &ldquo;website&rdquo; campaign link.
      </li>
      <li>
        <b>Partners and Direct outreach:</b> give each partner, and outreach,
        its own campaign code.
      </li>
    </ul>
  </div>
);

const AcquisitionDashboard: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const authGet = useCallback(
    async <T,>(path: string): Promise<T> => {
      const token = await getAccessTokenSilently();
      const res = await api.get<T>(path, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    [getAccessTokenSilently],
  );

  const [preset, setPreset] = useState<PresetId | null>('30d');
  const [range, setRange] = useState<DateRange>(() =>
    presetRange('30d', todayIso()),
  );
  const [compare, setCompare] = useState(false);
  const [by, setBy] = useState<ViewBy>('channel');
  const [view, setView] = useState<View>('funnel');
  const [focus, setFocus] = useState<StepId | null>(null);
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [targets, setTargets] = useState<Targets>(loadTargets);
  const [editTargets, setEditTargets] = useState(false);
  const [filters, setFilters] = useState<FiltersResponse | null>(null);
  const [clicksSince, setClicksSince] = useState<string | null>(null);
  const [cache, setCache] = useState<Record<string, Counts>>({});
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef(new Set<string>());
  const failed = useRef(new Set<string>());

  const prevRange = useMemo(() => shiftRange(range, -1), [range]);
  const today = todayIso();

  const pickPreset = (id: PresetId) => {
    setPreset(id);
    setRange(presetRange(id, todayIso()));
  };
  const setDates = (next: DateRange) => {
    setPreset(null);
    setRange(next);
  };

  useEffect(() => {
    localStorage.setItem(TARGETS_KEY, JSON.stringify(targets));
  }, [targets]);

  useEffect(() => {
    authGet<LinkClicksResponse>(
      `/activation-stats/link-clicks?invitedFrom=${ALL_TIME_FROM}&invitedTo=${todayIso()}`,
    )
      .then((r) => setClicksSince(r.trackedSince))
      .catch(() => setClicksSince(null));
  }, [authGet]);

  const filtersFrom = compare ? prevRange.from : range.from;
  useEffect(() => {
    let cancelled = false;
    authGet<FiltersResponse>(
      `/activation-stats/onboarding-filters?invitedFrom=${filtersFrom}&invitedTo=${range.to}`,
    )
      .then((r) => !cancelled && setFilters(r))
      .catch(() => !cancelled && setError('Failed to load channels'));
    return () => {
      cancelled = true;
    };
  }, [authGet, filtersFrom, range.to]);

  const tree = useMemo(() => (filters ? buildTree(filters) : []), [filters]);
  const units = useMemo(
    () => unitsFor(tree, selected, by),
    [tree, selected, by],
  );

  const needed = useMemo(
    () =>
      (compare ? [range, prevRange] : [range]).flatMap((r) =>
        units.map((u) => ({ key: cacheKey(r, u), r, u })),
      ),
    [compare, range, prevRange, units],
  );

  useEffect(() => {
    const todo = needed.filter(
      ({ key }) =>
        !(key in cache) &&
        !inflight.current.has(key) &&
        !failed.current.has(key),
    );
    if (todo.length === 0) return;
    todo.forEach(({ key }) => inflight.current.add(key));
    runLimited(
      todo.map(({ key, r, u }) => async () => {
        const q = `invitedFrom=${r.from}&invitedTo=${r.to}&${unitQuery(u)}`;
        try {
          const [ov, lc] = await Promise.all([
            authGet<OverviewResponse>(
              `/activation-stats/onboarding-overview?${q}`,
            ),
            authGet<LinkClicksResponse>(
              `/activation-stats/link-clicks?${q}`,
            ).catch(() => null),
          ]);
          setCache((c) => ({ ...c, [key]: toCounts(ov, lc) }));
        } catch {
          failed.current.add(key);
          setError('Some numbers failed to load; refresh to retry');
        } finally {
          inflight.current.delete(key);
        }
      }),
      4,
    );
  }, [needed, cache, authGet]);

  const pending = needed.filter(
    ({ key }) => !(key in cache) && !failed.current.has(key),
  ).length;

  const sum = useCallback(
    (r: DateRange, us: Unit[]) =>
      us.reduce((acc, u) => {
        const c = cache[cacheKey(r, u)];
        return c ? addCounts(acc, c) : acc;
      }, emptyCounts()),
    [cache],
  );

  const rows: Row[] = useMemo(() => {
    const groups = new Map<string, { label: string; units: Unit[] }>();
    for (const u of units) {
      const key =
        by === 'channel' ? u.channel : by === 'source' ? u.source : u.id;
      const label =
        by === 'channel'
          ? (CHANNELS.find((c) => c.id === u.channel)?.label ?? u.channel)
          : u.label;
      const g = groups.get(key) ?? { label, units: [] };
      g.units.push(u);
      groups.set(key, g);
    }
    return Array.from(groups.entries())
      .map(([key, g], i) => ({
        key,
        label: g.label,
        color:
          by === 'channel'
            ? (CHANNELS.find((c) => c.id === key)?.color ?? PALETTE[0])
            : PALETTE[i % PALETTE.length],
        cur: sum(range, g.units),
        prev: compare ? sum(prevRange, g.units) : null,
      }))
      .sort((a, b) => b.cur.mql - a.cur.mql);
  }, [units, by, sum, range, prevRange, compare]);

  const total = useMemo(() => sum(range, units), [sum, range, units]);
  const totalPrev = useMemo(
    () => (compare ? sum(prevRange, units) : null),
    [compare, sum, prevRange, units],
  );

  const clicksTracked = clicksSince != null && clicksSince <= range.from;
  const prevClicksTracked =
    clicksSince != null && clicksSince <= prevRange.from;
  // Clicks exist for at least part of the range: show them, with a note when
  // tracking started mid-range. Click→MQL rates and deltas still need the
  // whole range (and the previous one) tracked to be comparable.
  const clicksShown = clicksSince != null && clicksSince <= range.to;
  const clicksPartial = clicksShown && !clicksTracked;
  const clicksComparable = clicksTracked && prevClicksTracked;
  const stepsFor = (c: Counts, prev = false) =>
    stepsOf(c, targets, prev ? prevClicksTracked : clicksTracked);
  const totalSteps = stepsFor(total);
  const totalBlocker = blockerOf(totalSteps);

  const countOf = (c: Counts, id: StageId) => {
    if (id === 'tof') return c.clicks;
    if (id === 'tap') return c.taps;
    return id === 'ret' ? c.retained : c[id];
  };

  const byLabel =
    by === 'channel' ? 'Channel' : by === 'source' ? 'Source' : 'Code';

  const controls = (
    <>
      <div className='activation-controls'>
        <div className='day-selector'>
          {PRESETS.map(([id, label]) => (
            <button
              key={id}
              type='button'
              className={`day-btn ${preset === id ? 'active' : ''}`}
              onClick={() => pickPreset(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className='activation-controls'>
        <div className='acq-range-step'>
          <button
            type='button'
            className='day-btn'
            aria-label='Previous period'
            onClick={() => setDates(shiftRange(range, -1))}
          >
            ‹
          </button>
          <span className='acq-range-label'>{fmtRange(range)}</span>
          <button
            type='button'
            className='day-btn'
            aria-label='Next period'
            disabled={range.to >= today}
            onClick={() => setDates(shiftRange(range, 1))}
          >
            ›
          </button>
        </div>
        <label>
          From
          <UkDateInput
            value={range.from}
            max={range.to}
            onChange={(v) => setDates({ ...range, from: v })}
          />
        </label>
        <label>
          to
          <UkDateInput
            value={range.to}
            min={range.from}
            onChange={(v) => setDates({ ...range, to: v })}
          />
        </label>
        <label className='acq-check'>
          <input
            type='checkbox'
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
          />
          Compare with previous period
        </label>
        <div className='activation-control'>
          Channels
          <ChannelPicker
            tree={tree}
            selected={selected}
            onChange={setSelected}
          />
        </div>
        <div className='acq-seg' role='group' aria-label='Group rows'>
          {(Object.keys(BY_LABELS) as ViewBy[]).map((id) => (
            <button
              key={id}
              type='button'
              className={`day-btn ${by === id ? 'active' : ''}`}
              onClick={() => setBy(id)}
            >
              {BY_LABELS[id]}
            </button>
          ))}
        </div>
        <button
          type='button'
          className={`day-btn ${editTargets ? 'active' : ''}`}
          onClick={() => setEditTargets((o) => !o)}
        >
          Targets
        </button>
      </div>
      {editTargets && (
        <div className='activation-controls acq-targets'>
          {(Object.keys(STEP_LABELS) as StepId[]).map((id) => (
            <label key={id}>
              {STEP_LABELS[id]}
              <span className='acq-target-input'>
                <input
                  type='number'
                  min={0}
                  max={100}
                  step={0.1}
                  value={+(targets[id] * 100).toFixed(2)}
                  onChange={(e) =>
                    setTargets((t) => ({
                      ...t,
                      [id]: Math.max(0, Number(e.target.value) || 0) / 100,
                    }))
                  }
                />
                %
              </span>
            </label>
          ))}
          <button
            type='button'
            className='day-btn'
            onClick={() => setTargets(DEFAULT_TARGETS)}
          >
            Reset to Charlie&apos;s targets
          </button>
        </div>
      )}
      <div className='acq-tabs' role='tablist'>
        {(['funnel', 'bottlenecks', 'analysis'] as View[]).map((id) => (
          <button
            key={id}
            type='button'
            role='tab'
            aria-selected={view === id}
            className={`day-btn ${view === id ? 'active' : ''}`}
            onClick={() => setView(id)}
          >
            {id[0].toUpperCase() + id.slice(1)}
          </button>
        ))}
        {pending > 0 && <span className='acq-loading'>Loading…</span>}
      </div>
    </>
  );

  const stepCell = (s: Step | undefined) =>
    s && s.conv != null ? (
      <span
        className={`acq-pill ${s.prev < 5 ? 'none' : s.status}`}
        title={`Target ${pct(s.target)}${s.prev < 5 ? ' · fewer than 5 people, too few to judge' : ''}`}
      >
        {pct(s.conv)}
      </span>
    ) : (
      <span className='raw-n'>—</span>
    );

  const blockerCell = (steps: Step[], c: Counts) => {
    const b = blockerOf(steps);
    if (b)
      return (
        <span className={`acq-pill ${b.status}`}>
          {STEP_LABELS[b.id]} {pct(b.conv)} vs {pct(b.target)}
        </span>
      );
    return (
      <span className='raw-n'>
        {c.mql2 < 5 ? 'Too few to judge' : 'On target'}
      </span>
    );
  };

  const funnelView = (
    <>
      <section className='activation-section'>
        <h3>Acquisition funnel</h3>
        <p className='activation-note'>
          Everyone whose onboarding conversation started between{' '}
          {fmtRange(range)}, using the same counts as the Onboarding tab. Click
          a step to highlight it.
          {clicksPartial &&
            clicksSince &&
            ` Invite page views have only been recorded since ${fmtDay(clicksSince)}, so Top of funnel covers that part of the range only.`}
          {!clicksShown &&
            (clicksSince
              ? ` Invite page views are only recorded from ${fmtDay(clicksSince)}, after this range.`
              : ' No invite page views recorded yet.')}
        </p>
        <div className='onboarding-funnel-flow acq-flow'>
          {STAGES.map((st) => {
            const step = totalSteps.find((s) => s.id === st.id);
            const count =
              isInviteStage(st.id) && !clicksShown
                ? null
                : countOf(total, st.id);
            const max = Math.max(1, st.id === 'tap' ? total.clicks : total.mql);
            return (
              <button
                type='button'
                key={st.id}
                className={`flow-col flow-col--${st.id === 'ret' ? 'retention' : st.id === 'tof' ? 'entry' : 'core'}${count == null ? ' flow-col--empty' : ''}${focus === st.id ? ' acq-focus' : ''}`}
                onClick={() =>
                  !isInviteStage(st.id) &&
                  setFocus((f) => (f === st.id ? null : (st.id as StepId)))
                }
              >
                <div className='flow-count'>
                  {count ?? '—'}
                  {totalPrev &&
                    count != null &&
                    (!isInviteStage(st.id) || clicksComparable) && (
                      <Delta cur={count} prev={countOf(totalPrev, st.id)} />
                    )}
                </div>
                <div className='flow-track'>
                  <div
                    className='flow-bar'
                    style={{
                      height: `${count == null || st.id === 'tof' ? 0 : Math.min(100, (count / max) * 100)}%`,
                    }}
                  />
                </div>
                <div className='flow-label'>{st.name}</div>
                <div className='flow-detail'>
                  {st.id === 'ret'
                    ? `${total.retained} of ${total.retEligible} onboarded 8+ days ago`
                    : isInviteStage(st.id) && clicksPartial && clicksSince
                      ? `${st.sub} since ${fmtDay(clicksSince)}`
                      : st.sub}
                </div>
                {st.id === 'tap' && (
                  <div className='flow-detail'>
                    {pct(total.clicks > 0 ? total.taps / total.clicks : null)}{' '}
                    of
                    {' views'}
                  </div>
                )}
                {step && (
                  <div className='flow-detail'>
                    {stepCell(step)} target {pct(step.target)}
                  </div>
                )}
              </button>
            );
          })}
          {LOCKED_STAGES.map((name) => (
            <div key={name} className='flow-col flow-col--empty acq-locked'>
              <div className='flow-count'>—</div>
              <div className='flow-track' />
              <div className='flow-label'>{name}</div>
              <div className='flow-detail'>Not tracked yet</div>
            </div>
          ))}
        </div>
        <NotTracked />
      </section>
      <section className='activation-section'>
        <h3>{BY_LABELS[by]}</h3>
        <FunnelKey tree={tree} dots={by === 'channel'} />
        <div className='acq-table-wrap'>
          <table className='activation-table acq-table'>
            <thead>
              <tr>
                <th>{byLabel}</th>
                {STAGES.map((st) => (
                  <th
                    key={st.id}
                    className={focus === st.id ? 'acq-focus' : ''}
                  >
                    {st.name}
                  </th>
                ))}
                <th>Blocker</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const steps = stepsFor(r.cur);
                return (
                  <tr key={r.key}>
                    <td>
                      <i className='acq-dot' style={{ background: r.color }} />
                      {r.label}
                    </td>
                    {STAGES.map((st) => {
                      const step = steps.find((s) => s.id === st.id);
                      const tracked = !isInviteStage(st.id) || clicksShown;
                      const comparable =
                        !isInviteStage(st.id) || clicksComparable;
                      return (
                        <td
                          key={st.id}
                          className={focus === st.id ? 'acq-focus' : ''}
                        >
                          {tracked ? countOf(r.cur, st.id) : '—'}
                          {tracked && comparable && r.prev && (
                            <Delta
                              cur={countOf(r.cur, st.id)}
                              prev={countOf(r.prev, st.id)}
                            />
                          )}
                          {st.id === 'tap' && tracked && (
                            <div>
                              {pct(
                                r.cur.clicks > 0
                                  ? r.cur.taps / r.cur.clicks
                                  : null,
                              )}{' '}
                              of views
                            </div>
                          )}
                          {step && <div>{stepCell(step)}</div>}
                        </td>
                      );
                    })}
                    <td>{blockerCell(steps, r.cur)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                {STAGES.map((st) => {
                  const tracked = !isInviteStage(st.id) || clicksShown;
                  return (
                    <td
                      key={st.id}
                      className={focus === st.id ? 'acq-focus' : ''}
                    >
                      {tracked ? countOf(total, st.id) : '—'}
                      {st.id === 'tap' && tracked && (
                        <div>
                          {pct(
                            total.clicks > 0 ? total.taps / total.clicks : null,
                          )}{' '}
                          of views
                        </div>
                      )}
                    </td>
                  );
                })}
                <td>{blockerCell(totalSteps, total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </>
  );

  const ordered = focus
    ? [...rows].sort((a, b) => {
        const ca = stepsFor(a.cur).find((s) => s.id === focus)?.conv ?? 2;
        const cb = stepsFor(b.cur).find((s) => s.id === focus)?.conv ?? 2;
        return ca - cb;
      })
    : rows;

  const bottlenecksView = (
    <section className='activation-section'>
      <h3>Bottlenecks</h3>
      <p className='activation-note'>
        {totalBlocker
          ? `Biggest drop-off overall: ${STEP_LABELS[totalBlocker.id]}, ${pct(totalBlocker.conv)} against a ${pct(totalBlocker.target)} target.`
          : 'Every tracked step is on target overall.'}{' '}
        Green is on target, amber within 75% of it, red below that. Steps with
        fewer than 5 people are grey. Click a step to sort by it, worst first.
      </p>
      <div className='acq-table-wrap'>
        <table className='activation-table acq-table'>
          <thead>
            <tr>
              <th>{byLabel}</th>
              {(Object.keys(STEP_LABELS) as StepId[]).map((id) => (
                <th
                  key={id}
                  className={`acq-sortable${focus === id ? ' acq-focus' : ''}`}
                  onClick={() => setFocus((f) => (f === id ? null : id))}
                >
                  {STEP_LABELS[id]}
                  <div className='raw-n'>target {pct(targets[id])}</div>
                </th>
              ))}
              <th>Blocker</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((r) => {
              const steps = stepsFor(r.cur);
              const prevSteps = r.prev ? stepsFor(r.prev, true) : null;
              return (
                <tr key={r.key}>
                  <td>
                    <i className='acq-dot' style={{ background: r.color }} />
                    {r.label}
                  </td>
                  {steps.map((s) => {
                    const p = prevSteps?.find((x) => x.id === s.id);
                    return (
                      <td
                        key={s.id}
                        className={focus === s.id ? 'acq-focus' : ''}
                      >
                        {stepCell(s)}
                        {s.conv != null && (
                          <span className='raw-n'> of {s.prev}</span>
                        )}
                        {p && p.conv != null && (
                          <div className='raw-n'>was {pct(p.conv)}</div>
                        )}
                      </td>
                    );
                  })}
                  <td>{blockerCell(steps, r.cur)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );

  const days = Object.keys(total.daily).sort();
  const dayMax = Math.max(1, ...days.map((d) => total.daily[d] ?? 0));
  const w = 100 / Math.max(1, days.length);
  const analysisView = (
    <section className='activation-section'>
      <h3>First-message sign-ups (MQL2) per day</h3>
      <p className='activation-note'>
        Stacked by {byLabel.toLowerCase()}. Other metrics by day, spend and cost
        per sign-up need backend work or Meta access.
      </p>
      <div className='acq-legend'>
        {rows.map((r) => (
          <span key={r.key}>
            <i className='acq-dot' style={{ background: r.color }} />
            {r.label} ({r.cur.mql2})
          </span>
        ))}
      </div>
      {days.length > 0 && (
        <div className='acq-chart'>
          <svg viewBox='0 0 100 100' preserveAspectRatio='none' role='img'>
            {days.map((day, i) => {
              let acc = 0;
              return (
                <g key={day}>
                  {rows.map((r) => {
                    const n = r.cur.daily[day] ?? 0;
                    if (!n) return null;
                    const h = (n / dayMax) * 100;
                    acc += h;
                    return (
                      <rect
                        key={r.key}
                        x={i * w + w * 0.14}
                        width={w * 0.72}
                        y={100 - acc}
                        height={h}
                        fill={r.color}
                      >
                        <title>
                          {fmtDay(day)} · {r.label}: {n}
                        </title>
                      </rect>
                    );
                  })}
                </g>
              );
            })}
          </svg>
          <div className='signup-chart-axis'>
            <span>{fmtDay(days[0])}</span>
            <span>{fmtDay(days[days.length - 1])}</span>
          </div>
        </div>
      )}
    </section>
  );

  return (
    <div className='activation-dashboard acquisition-dashboard'>
      {error && <div className='msg error'>{error}</div>}
      {controls}
      {units.length === 0 && filters ? (
        <section className='activation-section'>
          <p className='activation-note'>Pick at least one channel.</p>
        </section>
      ) : view === 'funnel' ? (
        funnelView
      ) : view === 'bottlenecks' ? (
        bottlenecksView
      ) : (
        analysisView
      )}
    </div>
  );
};

export default AcquisitionDashboard;
